import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { loadExperimentVariation } from '../../api/experiments/experimentsApi'
import { getDefaultLogger } from '../../services/logger/logger'
import { ActiveExperimentVariation, ExperimentKey, ExperimentVariationFor } from './experiments'

type ExperimentState = {
  isLoading: boolean
  variation?: string
}

export type ExperimentVariationState<T extends ExperimentKey> = {
  isLoading: boolean
  variation: ExperimentVariationFor<T> | undefined
}

type ExperimentsContextValue = {
  experimentStates: Partial<Record<ExperimentKey, ExperimentState>>
  requestExperimentVariation: (experimentKey: ExperimentKey) => void
}

const ExperimentsContext = React.createContext<ExperimentsContextValue | undefined>(undefined)

type ExperimentProviderProps = {
  activeExperiments?: ActiveExperimentVariation[]
}

const toExperimentStates = (
  activeExperiments: ActiveExperimentVariation[]
): Partial<Record<ExperimentKey, ExperimentState>> =>
  activeExperiments.reduce(
    (states, { experimentKey, variationKey }) => ({
      ...states,
      [experimentKey]: { isLoading: false, variation: variationKey },
    }),
    {}
  )

export function ExperimentProvider(props: PropsWithChildren<ExperimentProviderProps>) {
  const [experimentStates, setExperimentStates] = useState(() => toExperimentStates(props.activeExperiments ?? []))
  const requestedExperimentKeys = useRef(
    new Set<ExperimentKey>(props.activeExperiments?.map(({ experimentKey }) => experimentKey))
  )

  useEffect(() => {
    if (props.activeExperiments !== undefined) {
      setExperimentStates(toExperimentStates(props.activeExperiments))
      requestedExperimentKeys.current = new Set(props.activeExperiments.map(({ experimentKey }) => experimentKey))
    }
  }, [props.activeExperiments])

  const requestExperimentVariation = useCallback((experimentKey: ExperimentKey) => {
    if (requestedExperimentKeys.current.has(experimentKey)) {
      return
    }

    requestedExperimentKeys.current.add(experimentKey)
    setExperimentStates((states) => ({ ...states, [experimentKey]: { isLoading: true } }))
    void loadExperimentVariation(experimentKey)
      .then((variation) => {
        setExperimentStates((states) => ({ ...states, [experimentKey]: { isLoading: false, variation } }))
        return undefined
      })
      .catch((error) => {
        getDefaultLogger().warn(`experiment ${experimentKey} could not be loaded`, error)
        setExperimentStates((states) => ({ ...states, [experimentKey]: { isLoading: false } }))
      })
  }, [])

  const value = useMemo(
    () => ({ experimentStates, requestExperimentVariation }),
    [experimentStates, requestExperimentVariation]
  )

  return <ExperimentsContext.Provider value={value}>{props.children}</ExperimentsContext.Provider>
}

function useExperimentsContext() {
  const context = useContext(ExperimentsContext)
  if (!context) {
    throw new Error('Experiment hooks must be used within an ExperimentProvider')
  }
  return context
}

export function useExperimentVariation<T extends ExperimentKey>(
  experimentKey: T,
  enabled: boolean
): ExperimentVariationState<T> {
  const { experimentStates, requestExperimentVariation } = useExperimentsContext()

  useEffect(() => {
    if (enabled) {
      requestExperimentVariation(experimentKey)
    }
  }, [enabled, experimentKey, requestExperimentVariation])

  if (!enabled) {
    return { isLoading: false, variation: undefined }
  }

  const state = experimentStates[experimentKey]
  return {
    isLoading: state?.isLoading ?? true,
    variation: state?.variation as ExperimentVariationFor<T> | undefined,
  }
}

export function useActiveExperimentVariation<T extends ExperimentKey>(
  experimentKey: T,
  enabled: boolean
): ExperimentVariationFor<T> | undefined {
  return useExperimentVariation(experimentKey, enabled).variation
}

export function useExperimentVariationIsActive(experiment: ActiveExperimentVariation, enabled: boolean): boolean {
  return useActiveExperimentVariation(experiment.experimentKey, enabled) === experiment.variationKey
}
